import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import updateLocale from "dayjs/plugin/updateLocale";

import { User } from "../model/user/User";
import { TFunction } from "i18next";
import { Range } from "../model/shared/dto/GenericWithRange";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(updateLocale);
dayjs.updateLocale("en", { weekStart: 1 });

export const standardDate = <T extends string | Date = string>(
  createdAt: string | Date | number,
  user?: User,
  showHour?: boolean,
  asDate?: boolean,
  timezoneOverride?: string,
): T => {
  try {
    if (!createdAt) {
      return "" as T;
    }

    const tz = timezoneOverride || user?.settings?.timezone;

    if (tz?.length) {
      const zone = tz || dayjs.tz.guess();
      // A Date/epoch-millis value, or a string already carrying an explicit
      // offset/Z, is an instant on its own - view it in `zone`. A naive
      // string (e.g. "2026-09-20T08:00:00", no offset) has no instant of
      // its own: `new Date(...)` would resolve it against the *browser's*
      // local timezone first, so the same value would render a different
      // wall-clock time for viewers in different browser timezones. Reading
      // it directly via `dayjs.tz` instead reinterprets its own digits as
      // wall-clock time in `zone`, independent of the viewer's browser.
      const hasOwnInstant =
        createdAt instanceof Date ||
        typeof createdAt === "number" ||
        /(?:Z|[+-]\d{2}:?\d{2})$/i.test(createdAt);

      const zoned = hasOwnInstant
        ? dayjs(createdAt).tz(zone)
        : dayjs.tz(createdAt, zone);

      if (asDate) {
        return zoned.format("YYYY-MM-DDTHH:mm:ss") as T;
      }

      return zoned.format(showHour ? "YYYY-MM-DD - HH:mm" : "YYYY-MM-DD") as T;
    }

    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    };

    if (showHour) {
      options.hour = "numeric";
      options.minute = "2-digit";
    }

    return new Date(createdAt).toLocaleDateString("en-GB", options) as T;
  } catch (e) {
    return "" as T;
  }
};

export const formatDateTimeWithoutTimezone = (date: Date | string) =>
  dayjs(date).format("YYYY-MM-DDTHH:mm:ss");

export const isValidDate = (date: any) => {
  // dayjs(number) reads it as epoch millis, so a plain numeric field value
  // (e.g. a quantity of 0 or 123) would otherwise be misidentified as a
  // valid date instead of being displayed as the number it is.
  if (typeof date === "number") {
    return false;
  }

  return (
    dayjs(date).isValid() ||
    dayjs(date, ["YYYY-MM-DD HH:mm", "YYYY-MM-DDTHH:mm"], true).isValid()
  );
};

export const rangeOptions = (t: TFunction) => {
  return Object.entries({ ALL: null, ...Range }).map(([key, value]) => ({
    label: t(`entries.table.entry.filters.time.${key.toLowerCase()}`),
    value: value as unknown as string,
  }));
};

export function formatDateKeyInZone(date: Date | string, tz: string) {
  return dayjs(date).tz(tz).format("YYYY-MM-DD");
}

export function formatDateKey(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`; // "2026-01-01"
}

/**
 * `val` for a matched field is the absolute-instant `Date` AtiraDatePickerFlat
 * already anchored to the input's configured timezone - serializing it as a
 * naive "YYYY-MM-DD HH:mm" would re-read it through the *browser's* local
 * timezone and silently shift the instant. `toISOString()` carries the
 * offset explicitly, so the API applies no further (mis)interpretation.
 */
export function parseDateKey(
  fields: Record<string, any>,
  isDate: (name: string) => boolean,
) {
  return Object.entries(fields || {}).reduce(
    (acc, [name, val]) => {
      if (!isDate(name) || val === undefined || val === null || val === "") {
        // dayjs(undefined) reads as "now", so an untouched optional date
        // field would otherwise get silently populated with the current
        // instant instead of staying empty.
        acc[name] = val;
        return acc;
      }

      const d = dayjs(val as any);
      acc[name] = d.isValid() ? d.toISOString() : String(val);
      return acc;
    },
    {} as Record<string, any>,
  );
}

export type HourRange = { from: string; to: string };

function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

/**
 * Splits an hours range into bookable slots of `minutesPerChunk`. Only emits
 * a slot when the full duration fits before the range ends - a trailing
 * fragment (e.g. a 90min slot with 30min left before closing) would promise
 * more time than is actually available, so it's dropped rather than
 * truncated. Shared by the booking picker and the settings-form preview so
 * both agree on exactly what a visitor will see.
 */
export function splitHourRange(
  range: HourRange,
  minutesPerChunk: number,
): Array<HourRange> {
  const start = timeToMinutes(range.from);
  const end = timeToMinutes(range.to);
  const result: Array<HourRange> = [];

  for (let m = start; m + minutesPerChunk <= end; m += minutesPerChunk) {
    result.push({
      from: minutesToTime(m),
      to: minutesToTime(m + minutesPerChunk),
    });
  }

  return result;
}

/**
 * Collapses overlapping/adjacent hours ranges (e.g. 02:00-04:30 and
 * 04:00-06:30, entered as two separate ranges by an admin) into disjoint
 * ones before slot-splitting. Without this, splitting each range
 * independently and concatenating the results can render the exact same
 * clock time as two separate slot buttons - or, if the ranges aren't
 * boundary-aligned, two genuinely overlapping-but-different-looking slots.
 */
/**
 * Whether a days/hours/slotDurationMinutes config actually produces at
 * least one bookable slot - a non-empty `hours` array alone doesn't
 * guarantee this (e.g. a single 09:00-09:15 range with a 30min duration
 * merges/splits down to zero slots), so gating booking-config validity on
 * `hours.length` alone lets a visitor-facing field through with nothing to
 * pick. Shared by the form and module-definition builders so both apply the
 * same rule.
 */
export function hasBookableSlots(config?: {
  hours?: HourRange[];
  slotDurationMinutes?: number;
}): boolean {
  if (!config?.hours?.length) {
    return false;
  }

  return mergeHourRanges(config.hours).some(
    (range) => splitHourRange(range, config.slotDurationMinutes || 30).length > 0,
  );
}

export function mergeHourRanges(ranges: HourRange[]): Array<HourRange> {
  const sorted = ranges
    .map((r) => ({ from: timeToMinutes(r.from), to: timeToMinutes(r.to) }))
    .filter((r) => r.to > r.from)
    .sort((a, b) => a.from - b.from);

  const merged: Array<{ from: number; to: number }> = [];

  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (last && range.from <= last.to) {
      last.to = Math.max(last.to, range.to);
    } else {
      merged.push({ ...range });
    }
  }

  return merged.map((r) => ({
    from: minutesToTime(r.from),
    to: minutesToTime(r.to),
  }));
}
